import React, { useEffect, useState } from 'react';
import { Users, BookOpen, MessageCircle, FileText, Sparkles, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../lib/adminApi';
import type { ApiAdminStats } from '../../types/admin';
import { LoadingSpinner } from '../common/Loading';
import { ErrorMessage } from '../common/Error';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ApiAdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.is_admin) {
      adminApi.getStats()
        .then(({ stats: s }) => setStats(s))
        .catch(err => {
          console.error('Error loading admin stats:', err);
          setError('Erro ao carregar estatísticas');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-purple-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-purple-200">Acesso Restrito</h2>
          <p className="text-purple-200/70">Você não tem permissão para acessar esta área.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <ErrorMessage message={error} />
      </div>
    );
  }

  const cards: Array<{ label: string; value: number; icon: React.ReactNode }> = [
    { label: 'Total de Usuários', value: stats?.users ?? 0, icon: <Users className="w-8 h-8 text-purple-300" /> },
    { label: 'Consultores Ativos', value: stats?.consultants ?? 0, icon: <Sparkles className="w-8 h-8 text-purple-300" /> },
    { label: 'Sessões de Chat', value: stats?.sessions ?? 0, icon: <MessageCircle className="w-8 h-8 text-purple-300" /> },
    { label: 'Candidaturas Pendentes', value: stats?.applications ?? 0, icon: <FileText className="w-8 h-8 text-purple-300" /> },
    { label: 'Posts Publicados', value: stats?.posts ?? 0, icon: <BookOpen className="w-8 h-8 text-purple-300" /> },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-purple-200">Painel Administrativo</h1>
          <p className="text-purple-200/70">Bem-vindo, {user.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map(card => (
          <div key={card.label} className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200/70">{card.label}</p>
                <h3 className="text-2xl font-bold text-purple-200">{card.value}</h3>
              </div>
              {card.icon}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;